import { formatNumber } from '@shared/format';
import { cn } from '@shared/ui/cn';

import { copy } from './copy';

/**
 * «قبل النشر» — what remains before the title can be published.
 *
 * **It is a reading, not a gate.** There is no button in it and it stops
 * nothing: a save produces a draft, and the requirement binds *publication*,
 * which happens from the content list. The line above the form already says
 * *that* something is required; this says *which*, because a form of six blocks
 * runs past two screens and an operator who knows a field is missing without
 * knowing which one has to walk the whole thing.
 *
 * **The membership is read from `REQUIRED_METADATA`**, not compiled in, so a
 * change to the setting reaches this list without a release. A member with no
 * wording renders as its own token rather than being dropped — a requirement the
 * operator cannot name is still a requirement, and a missing one is a hole.
 *
 * **Two signals, never colour alone**: a mark and the state written into each
 * row's own text.
 */
export function PublishChecklist({
  requiredMetadata,
  outstanding,
}: {
  requiredMetadata: readonly string[];
  outstanding: readonly string[];
}) {
  const remaining = outstanding.length;

  return (
    <section
      aria-labelledby="publish-checklist"
      className="grid gap-3 rounded-md border border-border bg-surface p-4"
    >
      <h2 id="publish-checklist" className="font-display text-h3 font-bold">
        {copy.checklist.heading}
      </h2>

      <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.checklist.lede}</p>

      <ul className="grid list-none gap-2 p-0">
        {requiredMetadata.map((member) => {
          const missing = outstanding.includes(member);

          return (
            <li key={member} className="flex items-center gap-2 text-sm">
              <span
                aria-hidden="true"
                className={cn('block size-2 shrink-0 rounded-full', missing ? 'bg-warning' : 'bg-success')}
              />
              <span className={missing ? 'text-ink' : 'text-ink-muted'}>
                {copy.checklist.fields[member] ?? member}
              </span>
              {/* The state in text, so the mark is never the only signal. */}
              <span className="sr-only">{missing ? copy.checklist.missing : copy.checklist.present}</span>
            </li>
          );
        })}
      </ul>

      <p aria-live="polite" className="mb-0 max-w-none text-xs text-ink-muted">
        {remaining === 0 ? copy.checklist.ready : copy.checklist.remaining(formatNumber(remaining))}
      </p>
    </section>
  );
}
